import React, { lazy, useEffect } from 'react';
import { Route, Switch, useHistory, useLocation, useParams, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useLocalStorage from 'react-use/lib/useLocalStorage.js';
import * as ROUTES from 'constants/routes.js';
import { ONE_TO_ONE_ONLY_ROUTES, PRODUCT_ROUTES, ON_DEMAND_ONLY_ROUTES, CHAT_ONLY_ROUTES, DELEGATED_CHAT_ROUTES } from 'constants/routes.js';
import useOrganizations from 'hooks/useOrganizations.js';
import {
  CHAT_PRODUCT,
  EXISTING_PRODUCTS,
  ONE_TO_MANY_PRODUCT,
  ONE_TO_ONE_PRODUCT,
  ON_DEMAND_PRODUCT,
} from 'constants/products.js';
import NotFoundPage from 'pages/NotFound/index.jsx';
import { ONE_TO_MANY_PERMISSIONS, ONE_TO_ONE_PERMISSIONS, VOD_PERMISSIONS, CHAT_PERMISSIONS } from 'shared-permissions';
import usePermissions from 'hooks/usePermissions.jsx';
import { getOnboardingStorageKey } from 'constants/onboarding.js';
import useOrgType from 'hooks/useOrgType.js';

const OrgStatsPage = lazy(() => import('pages/Stats/index.jsx'));
const OrgEmbedStatsPage = lazy(() => import('pages/StatsEmbed/index.jsx'));
const OrgDataReportsPage = lazy(() => import('pages/DataReports/index.jsx'));
const OrgDataExportJobsPage = lazy(() => import('pages/DataExportJobs/index.jsx'));
const OrgCallStatsPage = lazy(() => import('pages/StatsDetails/CallsStats/index.jsx'));
const QueuesPage = lazy(() => import('pages/Queues/index.jsx'));
const CallList = lazy(() => import('pages/CallList/index.jsx'));
const CallInsights = lazy(() => import('pages/CallInsights/index.jsx'));
const AgentToolPage = lazy(() => import('pages/AgentTool/index.jsx'));
const IframedChatPage = lazy(() => import('pages/IframedChatPage/index.jsx'));
const OldOrgShowsPage = lazy(() => import('pages/OldShows/index.jsx'));
const OrgShowsPage = lazy(() => import('pages/Shows/index.jsx'));
const OrgModulesPage = lazy(() => import('pages/Modules/index.jsx'));
const OrgFloatingWidgetPage = lazy(() => import('pages/FloatingWidget/index.jsx'));
const OrgUsersPage = lazy(() => import('pages/Users/index.jsx'));
const OrgLocalizationsPage = lazy(() => import('pages/Localizations/index.jsx'));
const OrgLocalizationsEditPage = lazy(() => import('pages/Localizations/LanguageLabelsList.jsx'));
const OrgCallBookingService = lazy(() => import('pages/CallBookingService/index.jsx'));
const OrgCallBookingServicesListPage = lazy(() => import('pages/CallBookingServices/index.jsx'));
const OrgCallBookingResources = lazy(() => import('pages/CallBookingResources/index.jsx'));
const OrgCallBookings = lazy(() => import('pages/CallBookings/index.jsx'));
const OrgCallBookingsNotifications = lazy(() => import('pages/CallBookingsNotifications/index.jsx'));
const OrgAgentAvailability = lazy(() => import('pages/AgentAvailability/index.jsx'));
const OrgUsersEditPage = lazy(() => import('pages/EditUser/index.jsx'));
const OrgUsersInvitePage = lazy(() => import('pages/CreateUser/index.jsx'));
const OrgGeneralSettingsPage = lazy(() => import('pages/GeneralSettings/index.jsx'));
const OrgShowPage = lazy(() => import('pages/Show/index.jsx'));
const OrgLandingPage = lazy(() => import('pages/LandingPage/index.jsx'));
const OrgProfilePage = lazy(() => import('pages/Profile/index.jsx'));
const OrgIntegrationsPage = lazy(() => import('pages/AppIntegrations/index.jsx'));
const CustomRtmpIntegration = lazy(() => import('pages/CustomRtmpIntegration/index.jsx'));
const FacebookIntegration = lazy(() => import('pages/FacebookIntegration/index.jsx'));
const InstagramIntegration = lazy(() => import('pages/InstagramIntegration/index.jsx'));
const YouTubeIntegration = lazy(() => import('pages/YouTubeIntegration/index.jsx'));
const OrgOneToManyHomePage = lazy(() => import('pages/Home/OneToManyHome.jsx'));
const OrgOneToOneHomePage = lazy(() => import('pages/Home/OneToOneHome.jsx'));
const OrgChatHomePage = lazy(() => import('pages/Home/ChatHome.jsx'));
const DepartmentsPage = lazy(() => import('pages/Departments/index.jsx'));
const ContactsAdminPage = lazy(() => import('pages/ContactsAdmin/index.jsx'));
const OrgApiKeysPage = lazy(() => import('pages/ApiKeys/index.jsx'));
const OrgWebhooksPage = lazy(() => import('pages/Webhooks/index.jsx'));
const OrgCallConfigurationsPage = lazy(() => import('pages/CallConfigurations/index.jsx'));
const UnauthorizedPage = lazy(() => import('pages/UnauthorizedPage/401.jsx'));
const OneToOneUpsell = lazy(() => import('pages/UnauthorizedPage/OneToOneUpsell.jsx'));
const OneToManyUpsell = lazy(() => import('pages/UnauthorizedPage/OneToManyUpsell.jsx'));

const ThemesOneToOnePage = lazy(() => import('pages/Theming/OneToOneThemes.jsx'));
const ThemesOneToManyPage = lazy(() => import('pages/Theming/OneToManyThemes.jsx'));
const ThemingOneToOnePage = lazy(() => import('pages/Theming/OneToOneTheming.jsx'));
const ThemingOneToManyPage = lazy(() => import('pages/Theming/OneToManyTheming.jsx'));
const ThemesVODPage = lazy(() => import('pages/Theming/VODThemes.jsx'));
const ThemingVODPage = lazy(() => import('pages/Theming/VODTheming.jsx'));
const ThemesInternalPage = lazy(() => import('pages/Theming/InternalThemes.jsx'));
const ThemingInternalPage = lazy(() => import('pages/Theming/InternalTheming.jsx'));

const ProductScraperTestPage = lazy(() => import('pages/ProductScraperTest/index.jsx'));
const GroupsPage = lazy(() => import('pages/Groups/index.jsx'));
const GroupPage = lazy(() => import('pages/Group/index.jsx'));
const CallBackground = lazy(() => import('pages/CallBackground/index.jsx'));
const OneToManyAIPage = lazy(() => import('pages/AIPage/index.jsx'));
const ProductFeedPage = lazy(() => import('pages/ProductFeed/index.jsx'));
const CatalogProductsPage = lazy(() => import('pages/CatalogProducts/index.jsx'));

const VideosVODPage = lazy(() => import('pages/Videos/index.jsx'));
const ProductsPage = lazy(() => import('pages/Products/index.jsx'));
const DistributionsVODPage = lazy(() => import('pages/Distributions/index.jsx'));
const DistributionVODPage = lazy(() => import('pages/Distribution/index.jsx'));
const GetStarted = lazy(() => import('pages/Vod/GetStarted/index.jsx'));
const Actions = lazy(() => import('pages/Actions/index.jsx'));
const PlaylistsPage = lazy(() => import('pages/Playlists/index.jsx'));
const PlaylistCreatePage = lazy(() => import('pages/Playlists/PlaylistCreatePage.jsx'));
const PlaylistsEditPage = lazy(() => import('pages/Playlists/PlaylistsEditPage.jsx'));
const PlacementsPage = lazy(() => import('pages/Placements/index.jsx'));
const PlacementsEditPage = lazy(() => import('pages/Placements/PlacementsEditPage.jsx'));
const PlacementsCreatePage = lazy(() => import('pages/Placements/PlacementCreatePage.jsx'));
const AppearancesPage = lazy(() => import('pages/Appearances/index.jsx'));
const AppearancesEditPage = lazy(() => import('pages/Appearances/AppearancesEditPage.jsx'));

const getOrganizationById = (organizations, orgId) => organizations.find((org) => org.id === orgId);

export const getNewRoute = ({ orgId, selectedProduct, pathname, newOrgId, newSelectedProduct }) => {
  if (!selectedProduct) return pathname.replace(`${orgId}`, `${newOrgId}/${newSelectedProduct}`);
  return pathname.replace(`${orgId}/${selectedProduct}`, `${newOrgId}/${newSelectedProduct}`);
};

export const getProductForRoute = (routePart, organizationFeatures) => {
  const orgHasOneToManyProduct = organizationFeatures?.shows?.enabled;
  const orgHasOneToOneProduct = organizationFeatures?.calls?.enabled;
  const orgHasOnDemandProduct = organizationFeatures?.onDemand?.enabled;
  const orgHasChatProduct = organizationFeatures?.chat?.enabled;

  const productByOrgFeatures =
    (orgHasOneToManyProduct && ONE_TO_MANY_PRODUCT) ||
    (orgHasOneToOneProduct && ONE_TO_ONE_PRODUCT) ||
    (orgHasOnDemandProduct && ON_DEMAND_PRODUCT) ||
    (orgHasChatProduct && CHAT_PRODUCT);

  if (ONE_TO_ONE_ONLY_ROUTES.some((route) => route.includes(routePart))) return ONE_TO_ONE_PRODUCT;
  if (ON_DEMAND_ONLY_ROUTES.some((route) => route.includes(routePart))) return ON_DEMAND_PRODUCT;
  if (CHAT_ONLY_ROUTES.some((route) => route.includes(routePart))) return CHAT_PRODUCT;

  return productByOrgFeatures;
};

const OrgRouter = () => {
  const history = useHistory();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const isSuperadmin = useSelector((state) => state.sessionState.authUser?.isSuperadmin);
  const masqueradeMode = useSelector((state) => state.ui.masqueradeMode);
  const { orgId, selectedProduct } = useParams();
  const { data: organizations, isLoading } = useOrganizations();

  const [trialOnboardingState] = useLocalStorage(getOnboardingStorageKey(orgId));
  const isTrialOnboardingComplete = trialOnboardingState?.isComplete === true;

  const organization = getOrganizationById(organizations, orgId);
  const organizationFeatures = organization?.features;
  const orgHasOneToManyProduct = organizationFeatures?.shows?.enabled;
  const orgHasOneToOneProduct = organizationFeatures?.calls?.enabled;
  const orgHasChatProduct = organizationFeatures?.chat?.enabled;
  const newShowsListEnabled = organizationFeatures?.newShowsList?.enabled;
  const showStatsDataReportsEnabled = organizationFeatures?.showStatsDataReports?.enabled;
  const onBoardingGuideEnabled = organizationFeatures?.onBoardingGuide?.enabled;
  const orgHasOnDemandProduct = organizationFeatures?.vodPage?.enabled;
  const productCatalogEnabled = organizationFeatures?.productCatalog?.enabled;
  const productCatalogProductsLiveEnabled = organizationFeatures?.productCatalogProductsLive?.enabled;
  const productCatalogProductsVODEnabled = organizationFeatures?.productCatalogProductsVOD?.enabled;
  const productCatalogProductsCallsEnabled = organizationFeatures?.productCatalogProductsCalls?.enabled;


  const orgType = useOrgType();
  const isTrialOrg = orgType === 'trial';
  const redirectTrialToOnboarding = isTrialOrg && !isTrialOnboardingComplete && onBoardingGuideEnabled;

  const hasOneToOnePermission =
    isSuperadmin || Object.values(ONE_TO_ONE_PERMISSIONS).some((permission) => hasPermission(permission));

  const hasOneToManyPermission =
    isSuperadmin || Object.values(ONE_TO_MANY_PERMISSIONS).some((permission) => hasPermission(permission));

  const hasOnDemandPermission =
    isSuperadmin || Object.values(VOD_PERMISSIONS).some((permission) => hasPermission(permission));

  const hasChatPermission =
    isSuperadmin || Object.values(CHAT_PERMISSIONS).some((permission) => hasPermission(permission));

  useEffect(() => {
    if (isLoading) return;
    const hasAccessToSelectedProduct = (product) => {
      return (
        (product === ONE_TO_ONE_PRODUCT && hasOneToOnePermission) ||
        (product === ONE_TO_MANY_PRODUCT && hasOneToManyPermission) ||
        (product === ON_DEMAND_PRODUCT && hasOnDemandPermission) ||
        (product === CHAT_PRODUCT && hasChatPermission)
      );
    };
    const latestProduct = localStorage.getItem('bambuser.latestProduct');

    const getSelectedProductRedirect = () => {
      if (hasAccessToSelectedProduct(selectedProduct)) return selectedProduct;
      if (latestProduct === ONE_TO_ONE_PRODUCT && orgHasOneToOneProduct && hasOneToOnePermission) {
        return ONE_TO_ONE_PRODUCT;
      }
      if (latestProduct === ONE_TO_MANY_PRODUCT && orgHasOneToManyProduct && hasOneToManyPermission) {
        return ONE_TO_MANY_PRODUCT;
      }
      if (latestProduct === ON_DEMAND_PRODUCT && orgHasOnDemandProduct && hasOnDemandPermission) {
        return ON_DEMAND_PRODUCT;
      }
      if (latestProduct === CHAT_PRODUCT && orgHasChatProduct && hasChatPermission) {
        return CHAT_PRODUCT;
      }
      if (orgHasOneToManyProduct && hasOneToManyPermission) return ONE_TO_MANY_PRODUCT;
      if (orgHasOneToOneProduct && hasOneToOnePermission) return ONE_TO_ONE_PRODUCT;
      if (orgHasOnDemandProduct && hasOnDemandPermission) return ON_DEMAND_PRODUCT;
      if (orgHasChatProduct && hasChatPermission) return CHAT_PRODUCT;

      return '';
    };

    const selectedProductRedirect = getSelectedProductRedirect();

    const redirectToOrgProduct = () => {
      if (selectedProduct && !EXISTING_PRODUCTS.includes(selectedProduct)) {
        const newSelectedProduct = getProductForRoute(selectedProduct, organizationFeatures);
        const newRoute = location.pathname.replace(`${orgId}`, `${organization.id}/${newSelectedProduct}`);

        history.replace(newRoute + window.location.search);
        return;
      }
      if (orgId === organization.id) {
        if (selectedProduct === ONE_TO_MANY_PRODUCT && !orgHasOneToManyProduct) {
          return history.replace(ROUTES.buildRoute(ROUTES.ORG_ONE_TO_MANY_UPSELL, { orgId }));
        }
        if (selectedProduct === ONE_TO_ONE_PRODUCT && !orgHasOneToOneProduct) {
          return history.replace(ROUTES.buildRoute(ROUTES.ORG_ONE_TO_ONE_UPSELL, { orgId }));
        }
        if (selectedProduct === ON_DEMAND_PRODUCT && !orgHasOnDemandProduct) {
          return history.replace(ROUTES.buildRoute(ROUTES.ORG_ONE_TO_MANY_UPSELL, { orgId }));
        }
      }

      if (orgId === organization.id && selectedProduct === selectedProductRedirect) return;

      if (!selectedProductRedirect) {
        const product =
          selectedProduct ||
          (orgHasOneToManyProduct && ONE_TO_MANY_PRODUCT) ||
          (orgHasOneToOneProduct && ONE_TO_ONE_PRODUCT) ||
          (orgHasOnDemandProduct && ON_DEMAND_PRODUCT) ||
          (orgHasChatProduct && CHAT_PRODUCT);
        return history.replace(ROUTES.buildRoute(ROUTES.ORG_401, { orgId, selectedProduct: product }));
      }

      if (!hasAccessToSelectedProduct(ONE_TO_MANY_PRODUCT) && selectedProduct === ONE_TO_MANY_PRODUCT) {
        return history.replace(ROUTES.buildRoute(ROUTES.ORG_401, { orgId, selectedProduct }));
      }

      if (!hasAccessToSelectedProduct(ONE_TO_ONE_PRODUCT) && selectedProduct === ONE_TO_ONE_PRODUCT) {
        return history.replace(ROUTES.buildRoute(ROUTES.ORG_401, { orgId, selectedProduct }));
      }

      if (!hasAccessToSelectedProduct(ON_DEMAND_PRODUCT) && selectedProduct === ON_DEMAND_PRODUCT) {
        return history.replace(ROUTES.buildRoute(ROUTES.ORG_401, { orgId, selectedProduct }));
      }

      if (!hasAccessToSelectedProduct(CHAT_PRODUCT) && selectedProduct === CHAT_PRODUCT) {
        return history.replace(ROUTES.buildRoute(ROUTES.ORG_401, { orgId, selectedProduct }));
      }

      const newRoute = getNewRoute({
        selectedProduct,
        orgId,
        pathname: location.pathname,
        newSelectedProduct: selectedProductRedirect,
        newOrgId: organization.id,
      });

      history.replace(newRoute + window.location.search);
    };

    if (!organization) return;
    if (selectedProductRedirect === selectedProduct) return;
    if (!orgHasOneToManyProduct && !orgHasOneToOneProduct && !orgHasOnDemandProduct && !orgHasChatProduct) return;
    redirectToOrgProduct();
  }, [
    isLoading,
    hasOneToManyPermission,
    hasOneToOnePermission,
    hasOnDemandPermission,
    history,
    location.pathname,
    orgHasOneToManyProduct,
    orgHasOneToOneProduct,
    orgHasOnDemandProduct,
    orgHasChatProduct,
    orgId,
    organization,
    organizationFeatures,
    selectedProduct,
    hasChatPermission,
  ]);

  useEffect(() => {
    localStorage.setItem('bambuser.latestOrgId', orgId);
  }, [orgId]);

  if (isLoading) return null;
  if (!organization && !masqueradeMode) return <NotFoundPage />;
  if (!orgHasOneToManyProduct && !orgHasOneToOneProduct && !orgHasOnDemandProduct && !orgHasChatProduct) return <UnauthorizedPage />;

  return (
    <Switch>
      <Route exact path={ROUTES.ORG_USERS}>
        <OrgUsersPage />
      </Route>
      <Route exact path={ROUTES.ORG_LOCALIZATION}>
        <OrgLocalizationsPage />
      </Route>
      <Route exact path={ROUTES.ORG_LOCALIZATION_EDIT}>
        <OrgLocalizationsEditPage />
      </Route>
      <Route exact path={ROUTES.ORG_USERS_EDIT}>
        <OrgUsersEditPage />
      </Route>
      <Route exact path={ROUTES.ORG_USERS_INVITE}>
        <OrgUsersInvitePage />
      </Route>
      <Route exact path={ROUTES.ORG_USER_PROFILE}>
        <OrgProfilePage />
      </Route>
      <Route exact path={ROUTES.ORG_THEMES}>
        <ThemesInternalPage />
      </Route>
      <Route exact path={ROUTES.ORG_API_KEYS}>
        <OrgApiKeysPage />
      </Route>
      <Route exact path={ROUTES.ORG_WEBHOOKS}>
        <OrgWebhooksPage />
      </Route>
      <Route exact path={ROUTES.ORG_INTEGRATIONS}>
        <OrgIntegrationsPage />
      </Route>
      <Route exact path={ROUTES.ORG_401}>
        <UnauthorizedPage />
      </Route>
      <Route exact path={ROUTES.ORG_THEME}>
        <ThemingInternalPage />
      </Route>
      <Route exact path={ROUTES.ORG_EMBED_STATS}>
        <OrgEmbedStatsPage />
      </Route>
      <Route exact path={ROUTES.ORG_GENERAL_SETTINGS}>
        <OrgGeneralSettingsPage />
      </Route>
      {productCatalogEnabled && (
        <Route exact path={[ROUTES.ORG_PRODUCT_FEED, ROUTES.ORG_PRODUCT_FEED_NEW, ROUTES.ORG_PRODUCT_FEED_ID]}>
          <ProductFeedPage />
        </Route>
      )}
      {(productCatalogProductsLiveEnabled || productCatalogProductsCallsEnabled) && (
        <Route exact path={[ROUTES.ORG_PRODUCTS, ROUTES.ORG_PRODUCTS_ID, ROUTES.ORG_PRODUCTS_NEW]}>
          <CatalogProductsPage />
        </Route>
      )}

      <Route path={ROUTES.LIVE}>
        <Switch>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_MANY_HOME}>
            <OrgOneToManyHomePage />
          </Route>
          {newShowsListEnabled && (
            <Route exact path={PRODUCT_ROUTES.ORG_SHOWS}>
              <OrgShowsPage />
            </Route>
          )}
          {!newShowsListEnabled && (
            <Route exact path={PRODUCT_ROUTES.ORG_SHOWS}>
              <OldOrgShowsPage />
            </Route>
          )}
          <Route exact path={PRODUCT_ROUTES.ORG_SHOW}>
            <OrgShowPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_MODULES}>
            <OrgModulesPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_LANDING_PAGE}>
            <OrgLandingPage />
          </Route>
          <Route exact path={[PRODUCT_ROUTES.ORG_FLOATING_WIDGET, PRODUCT_ROUTES.ORG_FLOATING_WIDGET_SETTINGS]}>
            <OrgFloatingWidgetPage />
          </Route>
          {showStatsDataReportsEnabled && (
            <Route exact path={PRODUCT_ROUTES.ORG_DATA_REPORTS}>
              <OrgDataReportsPage />
            </Route>
          )}
          <Route exact path={PRODUCT_ROUTES.ORG_DATA_EXPORT_JOBS}>
            <OrgDataExportJobsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_STATS}>
            <OrgStatsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_FACEBOOK_INTEGRATION}>
            <FacebookIntegration />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_INSTAGRAM_INTEGRATION}>
            <InstagramIntegration />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_YOUTUBE_INTEGRATION}>
            <YouTubeIntegration />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CUSTOM_RTMP_INTEGRATION}>
            <CustomRtmpIntegration />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_MANY_THEMES}>
            <ThemesOneToManyPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_MANY_THEMING}>
            <ThemingOneToManyPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_MANY_UPSELL}>
            <OneToManyUpsell />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_PRODUCT_SCRAPER}>
            <ProductScraperTestPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_MANY_AI}>
            <OneToManyAIPage />
          </Route>
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
      </Route>
      <Route path={ROUTES.CALLS}>
        <Switch>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_ONE_HOME}>
            <OrgOneToOneHomePage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_STATS}>
            <OrgCallStatsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_QUEUES}>
            <QueuesPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_LIST}>
            <CallList />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_INSIGHTS}>
            <CallInsights />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_AGENT_TOOL}>
            <AgentToolPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_ONE_UPSELL}>
            <OneToOneUpsell />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_BOOKING_SERVICES}>
            <OrgCallBookingServicesListPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_BOOKING_SERVICE}>
            <OrgCallBookingService />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_BOOKING_NOTIFICATIONS}>
            <OrgCallBookingsNotifications />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_BOOKING_RESOURCES}>
            <OrgCallBookingResources />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_BOOKINGS}>
            <OrgCallBookings />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_AGENT_AVAILABILITY}>
            <OrgAgentAvailability />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_GROUPS}>
            <GroupsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_GROUP}>
            <GroupPage />
          </Route>
          <Route exact path={ROUTES.ORG_CALL_CONFIGURATIONS}>
            <OrgCallConfigurationsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CALL_BACKGROUND}>
            <CallBackground />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_ONE_THEMES}>
            <ThemesOneToOnePage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ONE_TO_ONE_THEMING}>
            <ThemingOneToOnePage />
          </Route>
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
      </Route>
      <Route path={ROUTES.ON_DEMAND}>
        <Switch>
          {/*<Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_HOME}>
            <OrgOnDemandHomePage />
          </Route>*/}
          <Redirect
            exact
            from={PRODUCT_ROUTES.ORG_ON_DEMAND_HOME}
            to={
              redirectTrialToOnboarding
                ? PRODUCT_ROUTES.ORG_ON_DEMAND_GET_STARTED
                : PRODUCT_ROUTES.ORG_ON_DEMAND_VIDEOS
            }
          />
          <Route
            exact
            path={[
              PRODUCT_ROUTES.ORG_ON_DEMAND_VIDEO_SETUP,
              PRODUCT_ROUTES.ORG_ON_DEMAND_VIDEOS_EDIT_NEW,
              PRODUCT_ROUTES.ORG_ON_DEMAND_VIDEOS,
              PRODUCT_ROUTES.ORG_ON_DEMAND_HOME,
            ]}
          >
            <VideosVODPage />
          </Route>
          <Route exact path={[PRODUCT_ROUTES.ORG_ON_DEMAND_PRODUCT_SETUP, PRODUCT_ROUTES.ORG_ON_DEMAND_PRODUCTS, PRODUCT_ROUTES.ORG_ON_DEMAND_PRODUCT_NEW]}>
            {productCatalogProductsVODEnabled ? <CatalogProductsPage /> : <ProductsPage />}
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_DISTRIBUTION_PAGES}>
            <DistributionsVODPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_DISTRIBUTION_CONTAINERS}>
            <DistributionVODPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_DISTRIBUTION_CONTAINER}>
            <DistributionVODPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_DATA_EXPORT_JOBS}>
            <OrgDataExportJobsPage />
          </Route>
          {/*<Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_STATS}>
            <StatsVODPage />
          </Route>*/}
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_SETTINGS}>
            <div />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_GET_STARTED}>
            <GetStarted />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_ACTIONS}>
            <Actions />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_ACTIONS_SETUP}>
            <Actions />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_THEMES}>
            <ThemesVODPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_THEMING}>
            <ThemingVODPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_INSTAGRAM_INTEGRATION}>
            <InstagramIntegration />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_PRODUCT_SCRAPER}>
            <ProductScraperTestPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_PLAYLISTS}>
            <PlaylistsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_PLAYLISTS_NEW}>
            <PlaylistCreatePage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_PLAYLISTS_EDIT}>
            <PlaylistsEditPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_PLACEMENTS}>
            <PlacementsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_PLACEMENTS_NEW}>
            <PlacementsCreatePage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_PLACEMENTS_EDIT}>
            <PlacementsEditPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_APPEARANCES}>
            <AppearancesPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_ON_DEMAND_APPEARANCES_EDIT}>
            <AppearancesEditPage />
          </Route>
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
      </Route>
      <Route path={ROUTES.CHAT}>
        <Switch>
          <Route exact path={PRODUCT_ROUTES.ORG_CHAT_HOME}>
            <OrgChatHomePage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CHAT_STATS}>
            <OrgEmbedStatsPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CHAT_CONTACTS}>
            <ContactsAdminPage />
          </Route>
          <Route exact path={PRODUCT_ROUTES.ORG_CHAT_DEPARTMENTS}>
            <DepartmentsPage />
          </Route>
          <Route exact path={DELEGATED_CHAT_ROUTES.map(delegatedRoute => delegatedRoute.route)}>
            <IframedChatPage />
          </Route>
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
      </Route>
    </Switch>
  );
};

export default OrgRouter;
